import axios from "axios";
import { useState, useRef } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/breadcrumbs";

const TranscriptGeneration = () => {
  const [urlInputValue, setUrlInputValue] = useState("");
  const [data, setData] = useState("");
  const [loading, setLoading] = useState(false);

  const pollingRef = useRef(null);

  const startTranscriptGeneration = async (url) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/video-finder-transcript-generation`;
    const response = await axios.post(
      endpoint,
      { url },
      { headers: { "Content-Type": "application/json" } },
    );
    return response.data;
  };

  const fetchTranscriptById = async (id) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/video-finder-transcript-generation?id=${id}`;
    const response = await axios.get(endpoint);
    return response.data;
  };

  const pollTranscriptStatus = (id) => {
    pollingRef.current = setInterval(async () => {
      try {
        const result = await fetchTranscriptById(id);
        if (result?.data?.status === "COMPLETED") {
          clearInterval(pollingRef.current);
          if (result.data.transcript) {
            setData(result.data.transcript || "Transcript not available");
            toast.success("Transcript generated successfully!");
          } else {
            setData("");
            toast.warning("Transcript not available");
          }
          setLoading(false);
        } else if (!["INPROGRESS", "NOT_STARTED"].includes(result?.data?.status)) {
          clearInterval(pollingRef.current);
          setLoading(false);
          toast.error("Transcript generation failed");
        }
      } catch (err) {
        clearInterval(pollingRef.current);
        setLoading(false);
        toast.error("Error while fetching transcript");
      }
    }, 5 * 1000);
  };

  const isValidYoutubeUrl = (url) => {
    try {
      const parsed = new URL(url);

      // Must be HTTPS
      // if (parsed.protocol !== "https:") return false;

      // Long YouTube URL: https://www.youtube.com/watch?v=xxxx
      if (
        parsed.hostname === "www.youtube.com" &&
        parsed.pathname === "/watch" &&
        parsed.searchParams.has("v")
      ) {
        return true;
      }

      // Embed URL: https://www.youtube.com/embed/xxxx
      if (parsed.hostname === "www.youtube.com" && parsed.pathname.startsWith("/embed/")) {
        return true;
      }

      // Short URL: https://youtu.be/xxxx
      if (
        parsed.hostname === "youtu.be" &&
        parsed.pathname.length > 1 // has an ID
      ) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const handleGenerate = async () => {
    if (!urlInputValue) {
      toast.error("Please enter a URL");
      return;
    }

    if (!isValidYoutubeUrl(urlInputValue.trim())) {
      toast.error("Please enter a valid URL");
      return;
    }

    setLoading(true);
    setData("");

    try {
      const { data: id } = await startTranscriptGeneration(urlInputValue);
      if (!id) throw new Error("No data returned from transcript generation API");
      pollTranscriptStatus(id);
    } catch (error) {
      console.error("Error generating transcript:", error);
      toast.error(error.response?.data?.message || "Failed to generate transcript");
      setLoading(false);
    }
  };
  const handleCopy = () => {
    if (data) {
      navigator.clipboard
        .writeText(data)
        .then(() => toast.success("Transcript copied to clipboard!"))
        .catch(() => toast.error("Failed to copy transcript."));
    }
  };
  return (
    <Container className="my-3">
      <Breadcrumbs />
      <p className="section-title mb-5">Transcript Generation</p>

      <div className="d-flex flex-wrap flex-md-nowrap justify-content-between align-items-center mb-3 gap-3">
        <div className="d-flex align-items-center w-100" style={{ maxWidth: "1120px" }}>
          <Form.Group controlId="url-textbox" className="d-flex align-items-center w-100">
            <Form.Control
              type="text"
              placeholder="Enter URL"
              value={urlInputValue}
              onChange={(e) => setUrlInputValue(e.target.value)}
            />
          </Form.Group>
        </div>

        <div className="d-flex align-items-center">
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={loading || !urlInputValue}
            style={{ height: "48px" }}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Generating</span>
              </>
            ) : (
              "Generate"
            )}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div
            style={{
              textAlign: "left",
              marginTop: "20px",
              whiteSpace: "pre-wrap",
              padding: "20px 25px",
              border: "1px solid #E0E0E0",
            }}
          >
            {data}
          </div>

          {!["transcript not available"].includes(data?.toLocaleLowerCase()) && (
            <div className="w-100 d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                style={{ height: "48px" }}
                onClick={handleCopy}
                className="gap-2 hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Copy
              </Button>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default TranscriptGeneration;
